import { useMutation } from '@tanstack/react-query'
import { Image as ImageIcon, Loader, Upload, X } from 'lucide-react'
import type React from 'react'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'
import { axiosPrivate } from '@/shared/api/axios'
import { API } from '@/shared/constants/api'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/ui/button'
import { getErrorMessage } from '@/shared/utils/handle-api-error'

export interface UploadImageResponse {
  url: string
  filename: string
  size: number
  mimetype: string
}

interface ImageDropzoneProps {
  value?: string
  onChange: (url: string | null) => void
  disabled?: boolean
  placeholder?: string
  className?: string
  maxFileSize?: number // in MB
  acceptedTypes?: string[]
  /**
   * ☠️ **El `id` del `<input type="file">`, y NO es cosmético.**
   *
   * Estaba clavado en `image-upload-input`, y `handleClick` hace
   * `document.getElementById('image-upload-input')?.click()`. Con DOS dropzones en el mismo
   * formulario —que es exactamente lo que el proyecto tiene desde que hay logotipo e ícono de la
   * app— `getElementById` devuelve SIEMPRE el primero: clickear el segundo abría el selector del
   * primero y el archivo aterrizaba en el campo equivocado. No falla, no avisa: guarda el logo de
   * la app en el campo del logotipo de papelería.
   *
   * El default conserva el id viejo para no tocar a quien ya lo usa solo.
   */
  inputId?: string
  /**
   * QUÉ FORMA tiene que tener la imagen, si tiene que tener alguna.
   *
   * Dos cosas con un solo dato: se valida en el navegador ANTES de subir —decodificando la imagen,
   * que es lo único que puede medirla sin red— y viaja al servidor como `?spec=`, donde la regla se
   * vuelve a aplicar. La del navegador existe para no hacer subir cinco megas antes de decir que
   * no; la del servidor es la que vale, porque el endpoint lo llama cualquiera con sesión.
   */
  spec?: ImageSpec
}

/**
 * Los usos con forma declarada. Espeja `IMAGE_SPECS` de la API
 * (`apps/api-real-estate/src/attachments/domain/image-spec.ts`), que es donde la regla se hace
 * cumplir de verdad.
 */
export const ImageSpec = {
  /** El logo cuadrado que la app del cliente pinta sobre la portada: 1024×1024 exactos. */
  projectMark: 'project-mark'
} as const

export type ImageSpec = (typeof ImageSpec)[keyof typeof ImageSpec]

/** Lo que cada `spec` exige, del lado del navegador. */
const SPEC_RULES: Record<ImageSpec, { width: number; height: number }> = {
  [ImageSpec.projectMark]: { width: 1024, height: 1024 }
}

/**
 * Mide una imagen sin subirla.
 *
 * `createImageBitmap` y no un `<img>` con `onload`: decodifica fuera del hilo principal y no deja
 * un nodo suelto en el documento. El `objectUrl` se revoca en el `finally` — sin eso, cada intento
 * fallido deja el archivo entero retenido en memoria hasta que se recargue la pestaña.
 *
 * Un archivo que no se puede decodificar devuelve `null`, y quien llama lo trata como un rechazo:
 * un «no pude medirlo» tratado como «está bien» convertiría la validación en decorativa justo para
 * el archivo más sospechoso.
 */
async function measureImage(file: File): Promise<{ width: number; height: number } | null> {
  const objectUrl = URL.createObjectURL(file)
  try {
    const bitmap = await createImageBitmap(file)
    const size = { width: bitmap.width, height: bitmap.height }
    bitmap.close()
    return size
  } catch {
    return null
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

export const ImageDropzone: React.FC<ImageDropzoneProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder = 'Arrastra una imagen aquí o haz clic para seleccionar',
  className,
  maxFileSize = 5, // 5MB por defecto
  acceptedTypes = ['image/jpeg', 'image/png', 'image/webp'],
  inputId = 'image-upload-input',
  spec
}) => {
  const [isDragOver, setIsDragOver] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const uploadImageMutation = useMutation({
    mutationFn: async (file: File): Promise<UploadImageResponse> => {
      const formData = new FormData()
      formData.append('image', file)

      const res = await axiosPrivate.post<UploadImageResponse>(
        API.attachments.uploadImage,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          // El `spec` viaja al servidor además de validarse acá: la comprobación del navegador es
          // una ayuda, no una guarda — el endpoint lo llama cualquiera con sesión.
          params: spec ? { spec } : undefined
        }
      )
      return res.data
    },
    onSuccess: (data) => {
      toast.success('Imagen subida correctamente')
      onChange(data.url)
      setError(null)
    },
    onError: (error) => {
      const message = getErrorMessage(error)
      toast.error(message)
      setError(message)
    }
  })

  const validateFile = useCallback(
    (file: File): string | null => {
      // Validar tipo de archivo
      if (!acceptedTypes.includes(file.type)) {
        return `Tipo de archivo no permitido. Solo se permiten: ${acceptedTypes.map((type) => type.split('/')[1]).join(', ')}`
      }

      // Validar tamaño de archivo
      const fileSizeInMB = file.size / (1024 * 1024)
      if (fileSizeInMB > maxFileSize) {
        return `El archivo es demasiado grande. Tamaño máximo: ${maxFileSize}MB`
      }

      return null
    },
    [acceptedTypes, maxFileSize]
  )

  const handleFile = useCallback(
    async (file: File) => {
      setError(null)

      const validationError = validateFile(file)
      if (validationError) {
        setError(validationError)
        return
      }

      // Las dimensiones se miden ANTES de subir. El servidor las vuelve a medir —esto no lo
      // reemplaza— pero enterarse después de mandar cinco megas es enterarse tarde.
      const rule = spec ? SPEC_RULES[spec] : null
      if (rule) {
        const size = await measureImage(file)
        if (!size) {
          setError('No se pudo leer la imagen. Prueba con un archivo PNG, JPG o WebP.')
          return
        }
        if (size.width !== rule.width || size.height !== rule.height) {
          // Las medidas REALES en el mensaje: «no cumple» sin decir cuánto mide obliga a abrir la
          // imagen en otra herramienta para averiguar qué hay que cambiar.
          setError(
            `La imagen debe medir ${rule.width}×${rule.height} píxeles y esta mide ${size.width}×${size.height}.`
          )
          return
        }
      }

      uploadImageMutation.mutate(file)
    },
    [validateFile, uploadImageMutation, spec]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      setIsDragOver(false)

      if (disabled || uploadImageMutation.isPending) return

      const files = Array.from(e.dataTransfer.files)
      if (files.length > 0) {
        handleFile(files[0])
      }
    },
    [disabled, uploadImageMutation.isPending, handleFile]
  )

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      if (!disabled && !uploadImageMutation.isPending) {
        setIsDragOver(true)
      }
    },
    [disabled, uploadImageMutation.isPending]
  )

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(false)
  }, [])

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (files && files.length > 0) {
        handleFile(files[0])
      }
    },
    [handleFile]
  )

  const handleRemove = useCallback(() => {
    onChange(null)
    setError(null)
  }, [onChange])

  const handleClick = useCallback(() => {
    if (disabled || uploadImageMutation.isPending) return
    document.getElementById(inputId)?.click()
  }, [disabled, uploadImageMutation.isPending, inputId])

  return (
    <div className={cn('space-y-2', className)}>
      {/* Dropzone Area */}
      <div
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={cn(
          'relative border-2 border-dashed rounded-lg p-6 transition-all duration-200 cursor-pointer',
          'hover:border-primary/50 hover:bg-muted/50',
          {
            'border-primary bg-primary/5': isDragOver,
            'border-muted-foreground/25': !isDragOver && !value,
            'cursor-not-allowed opacity-50': disabled || uploadImageMutation.isPending,
            'border-primary bg-primary/10': value && !error,
            'border-red-500 bg-red-50': error
          }
        )}
      >
        <input
          id={inputId}
          type="file"
          accept={acceptedTypes.join(',')}
          onChange={handleInputChange}
          className="hidden"
          disabled={disabled || uploadImageMutation.isPending}
        />

        {uploadImageMutation.isPending ? (
          <div className="flex flex-col items-center justify-center text-center">
            <Loader className="h-8 w-8 animate-spin text-primary mb-2" />
            <p className="text-sm text-muted-foreground">Subiendo imagen...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center text-center relative">
            {/* Imagen de preview si existe */}
            {value && (
              <div className="relative mb-2">
                <img
                  src={value}
                  alt="Preview"
                  className="h-32 max-w-[80%] mx-auto object-contain rounded-lg border"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemove()
                  }}
                >
                  <X className="h-3 w-3" />
                </Button>
                {/* Overlay para drag over cuando hay imagen */}
                {isDragOver && (
                  <div className="absolute inset-0 bg-primary/20 rounded-lg flex items-center justify-center">
                    <Upload className="h-8 w-8 text-primary" />
                  </div>
                )}
              </div>
            )}

            {/* Área de contenido principal */}
            <div className="flex flex-col items-center">
              {!value && (
                <div className="mb-4">
                  {error ? (
                    <div className="p-3 rounded-full bg-red-100">
                      <X className="h-6 w-6 text-red-600" />
                    </div>
                  ) : (
                    <div className="p-3 rounded-full bg-muted">
                      {isDragOver ? (
                        <Upload className="h-6 w-6 text-primary" />
                      ) : (
                        <ImageIcon className="h-6 w-6 text-muted-foreground" />
                      )}
                    </div>
                  )}
                </div>
              )}

              <p className="text-sm font-medium text-foreground mb-1">
                {isDragOver
                  ? 'Suelta la imagen aquí'
                  : value
                    ? 'Haz clic o arrastra una nueva imagen para reemplazar'
                    : placeholder}
              </p>

              {!value && (
                <p className="text-xs text-muted-foreground">
                  Formatos permitidos:{' '}
                  {acceptedTypes.map((type) => type.split('/')[1].toUpperCase()).join(', ')}· Tamaño
                  máximo: {maxFileSize}MB
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
          {error}
        </div>
      )}
    </div>
  )
}
